import { useState } from 'react';

import { POSITIONS, POSITION_LABELS, SQUAD_QUOTA, formatCost } from './draftRules.js';
import './DraftPitch.css';

const kitSrc = (team) => `/static/img/kits/${team}.png`;

/*
 * Every current club has kit art, but a promoted side arrives before its
 * shirt does. Falling back to the club's code keeps that from rendering as
 * a broken-image icon.
 *
 * Detected by letting the image fail rather than by listing which files
 * exist: the list would have to be kept in step with the directory, and
 * adding a PNG later should just start working.
 */
function Kit({ team }) {
	const [failed, setFailed] = useState(false);

	if (failed) {
		return (
			<span className="draft-slot__kit-missing" aria-hidden="true">
				{team}
			</span>
		);
	}

	return (
		<img
			className="draft-slot__kit"
			src={kitSrc(team)}
			alt=""
			loading="lazy"
			width="56"
			height="72"
			onError={() => setFailed(true)}
		/>
	);
}

/*
 * The pitch, as fifteen addressable slots.
 *
 * Filled slots are the only controls. Substitutions are a two-step
 * selection rather than drag and drop: tap a player, tap the one to swap
 * them with. Dragging is unusable on a phone, which is where a team gets
 * picked. Swaps may cross positions whenever the resulting XI is legal,
 * which is what moves the shape — there is no separate formation control.
 */

function Slot({ slot, player, fixtures, editable, state, onSelect, onRemove }) {
	/* An empty slot is a gap in the squad, not a control: players are
	   picked from the list and land in the first free slot of their own
	   position, so there is nothing to click here. */
	if (!player) {
		return (
			<li className="draft-slot draft-slot--empty">
				<span className="draft-slot__button" aria-label={`${slot.position} slot, empty`}>
					<span className="draft-slot__position">{slot.position}</span>
				</span>
			</li>
		);
	}

	const next = fixtures?.[player.team_name] || [];
	const label = `${player.name}, ${player.team_name}, ${formatCost(player.cost)}${
		next.length ? `, ${next.map((f) => f.fixture).join(' and ')}` : ', no fixture'
	}`;

	return (
		<li className={`draft-slot${state ? ` draft-slot--${state}` : ''}`}>
			<button
				type="button"
				className="draft-slot__button"
				onClick={() => onSelect(slot)}
				disabled={!editable}
				aria-label={label}
				aria-pressed={state === 'selected' || undefined}
			>
				<span className="draft-slot__kit-wrap">
					<Kit team={player.team_name} />
				</span>
				<span className="draft-slot__name">
					<span className="draft-slot__name-text" title={player.name}>
						{player.name}
					</span>
				</span>
				{/* The fixture, not the price: the price is on the bar and in
				    the list, but who a player faces next is the thing you are
				    weighing while looking at the pitch. A blank gameweek shows
				    a dash rather than nothing, so the card keeps its shape. */}
				{next.length === 0 ? (
					<span className="draft-slot__fixture" data-difficulty="0">
						—
					</span>
				) : (
					next.map((fix, index) => (
						<span
							key={index}
							className="draft-slot__fixture"
							data-difficulty={fix.diff || undefined}
						>
							{fix.fixture}
						</span>
					))
				)}
			</button>
			{editable && (
				<button
					type="button"
					className="draft-slot__remove"
					onClick={() => onRemove(slot)}
					aria-label={`Remove ${player.name}`}
					title={`Remove ${player.name}`}
				>
					×
				</button>
			)}
		</li>
	);
}

export default function DraftPitch({
	squad,
	shape,
	bench,
	fixtures,
	editable = false,
	selection = null,
	canSwap,
	onSlotSelect,
	onSlotRemove,
}) {
	const slotsFor = (position, from, to) =>
		Array.from({ length: to - from }, (_, offset) => {
			const index = from + offset;
			return {
				position,
				index,
				starting: index < shape[position],
				player: squad[position][index],
			};
		});

	const same = (a, b) => a && b && a.position === b.position && a.index === b.index;

	const stateOf = (slot) => {
		if (same(selection, slot)) return 'selected';
		if (selection && editable && canSwap?.(selection, slot)) return 'eligible';
		return null;
	};

	const renderLine = (slots) => (
		<ul className="draft-pitch__line">
			{slots.map((slot) => (
				<Slot
					key={`${slot.position}-${slot.index}`}
					slot={slot}
					player={slot.player}
					fixtures={fixtures}
					editable={editable}
					state={stateOf(slot)}
					onSelect={onSlotSelect}
					onRemove={onSlotRemove}
				/>
			))}
		</ul>
	);

	/*
	 * The bench, in the order it will be used: reserve keeper first, then
	 * the outfield substitutes in their stored priority.
	 *
	 * Built from that order rather than by walking the position arrays,
	 * because the order is the point. `benchIndex` is the handle that lets
	 * two of them be swapped without touching the squad itself — which
	 * player is benched has not changed, only who comes on first.
	 */
	const filled = (bench || []).filter(Boolean).map((player) => ({
		position: player.position,
		index: squad[player.position].indexOf(player),
		starting: false,
		player,
	}));

	const spare = POSITIONS.flatMap((position) =>
		slotsFor(position, shape[position], SQUAD_QUOTA[position]),
	).filter((slot) => !slot.player);

	const isKeeper = (slot) => slot.position === 'GKP';
	const benchSlots = [
		...filled.filter(isKeeper),
		...spare.filter(isKeeper),
		...filled
			.filter((slot) => !isKeeper(slot))
			.map((slot, order) => ({ ...slot, benchIndex: order })),
		...spare.filter((slot) => !isKeeper(slot)),
	];

	return (
		<div className="draft-pitch">
			<div className="draft-pitch__field">
				{/* The pitch is the part that gets screenshotted and pasted
				    into a group chat, so it carries the mark. The formation
				    used to sit here and was only ever restating what the rows
				    already show. */}
				<span className="draft-pitch__brand">
					<img src="/static/img/logo.png" alt="" width="18" height="18" />
					Fantasy Nutmeg
				</span>
				{POSITIONS.map((position) => (
					<section
						key={position}
						className="draft-pitch__row"
						aria-label={`${POSITION_LABELS[position]} — on the pitch`}
					>
						{renderLine(slotsFor(position, 0, shape[position]))}
					</section>
				))}
			</div>

			<section className="draft-pitch__bench" aria-label="Substitutes">
				{renderLine(benchSlots)}
			</section>
		</div>
	);
}
